import React, { useReducer } from "react";
import { calculateCropRect, getBoxSize } from "@/react/utils";
import { moveCropRectByDirection } from "../components/Template/MoveUtils";
import { getCropRectForImageWithRotation } from "@/react/components/Template/common";
export const ACTIONS = {
    SET_TEMPLATE_INSTANCE: "set-template-instance",
    SET_TEMPLATE: "set-template",
    SET_RESOURCES: "set-resources",
    SET_PRODUCT: "set-product",
    PREVIOUS_PARTREF: "previous-partref",
    NEXT_PARTREF: "next-partref",
    SET_PARTREF: "set-partref",
    SET_YEAR: "set-year",
    SET_CURRENT_RESOURCE: "set-current-resource",
    UPDATE_CURRENT_IMAGE: "update-current-image",
    ROTATE_IMAGE_RESOURCE_LEFT: "rotate-resource-left",
    ROTATE_IMAGE_RESOURCE_RIGHT: "rotate-resource-right",
    SET_IMAGE: "set-image",
    SET_IMAGE_EFFECT: "set-image-effect",
    SET_IMAGE_BACKGROUND: "set-image-background",
    MOVE_IMAGE: "move-image",
    IMAGE_ZOOM_IN: "image-zoom-in",
    IMAGE_ZOOM_OUT: "image-zoom-out",
    UPDATE_CURRENT_TEXT: "update-current-text",
    SET_CURRENT_PARTREF: "set-current-partref",
    SET_SCALE_RATIO: "set-scale-ratio",
};
export const TemplateInstanceContext = React.createContext(undefined);
const templateInstanceReducer = (state, action) => {
    var _a, _b, _c, _d, _e, _f;
    let partRef, resource, resources;
    switch (action.type) {
        case ACTIONS.SET_SCALE_RATIO:
            return Object.assign(Object.assign({}, state), { scaleRatio: action.scaleRatio });
        case ACTIONS.SET_TEMPLATE_INSTANCE:
            return action.templateInstance;
        case ACTIONS.SET_TEMPLATE:
            return createInstance(action.template);
        case ACTIONS.SET_RESOURCES:
            return Object.assign(Object.assign({}, state), { resources: Object.assign(Object.assign({}, state.resources), action.resources) });
        case ACTIONS.SET_PRODUCT:
            return Object.assign(Object.assign({}, state), { product: action.product });
        case ACTIONS.SET_CURRENT_PARTREF:
            return Object.assign(Object.assign({}, state), { currentPartRefId: action.partRefId });
        case ACTIONS.SET_CURRENT_RESOURCE:
            return Object.assign(Object.assign({}, state), { currentResourceId: action.id });
        case ACTIONS.UPDATE_CURRENT_TEXT:
            if (!state.currentPartRefId || !state.currentResourceId) {
                return state;
            }
            partRef = getPartRefById(state.currentPartRefId, state.template);
            resource = getResource(partRef, state.currentResourceId, state);
            resources = Object.assign(Object.assign({}, state.resources), { [resource.fullId]: Object.assign(Object.assign({}, resource), { font: Object.assign(Object.assign({}, resource.font), action.font) }) });
            return Object.assign(Object.assign({}, state), { dirty: true, resources });
        case ACTIONS.UPDATE_CURRENT_IMAGE:
            if (!state.currentPartRefId || !state.currentResourceId) {
                return state;
            }
            partRef = getPartRefById(state.currentPartRefId, state.template);
            resource = getResource(partRef, state.currentResourceId, state);
            resources = Object.assign(Object.assign({}, state.resources), { [resource.fullId]: Object.assign(Object.assign({}, resource), { image: action.image }) });
            return Object.assign(Object.assign({}, state), { dirty: true, resources });
        case ACTIONS.ROTATE_IMAGE_RESOURCE_LEFT:
            return rotateImageResource(state, action.payload.partRefId, action.payload.resourceId, "left");
        case ACTIONS.ROTATE_IMAGE_RESOURCE_RIGHT:
            return rotateImageResource(state, action.payload.partRefId, action.payload.resourceId, "right");
        case ACTIONS.MOVE_IMAGE: {
            const partRef = getPartRefById(action.payload.partRefId, state.template);
            const resource = getResource(partRef, action.payload.resourceId, state);
            if (!resource || resource.type !== "IMAGE") {
                return state;
            }
            const renderPart = getFirstRenderPartByResource(resource, state.template);
            if (!renderPart) {
                return state;
            }
            const direction = action.payload.direction;
            const image = resource.image;
            const crop = (_a = image.cropRect) !== null && _a !== void 0 ? _a : {
                x: 0,
                y: 0,
                width: image.width,
                height: image.height,
            };
            const newCropRect = moveCropRectByDirection({
                crop,
                image: { width: image.width, height: image.height },
                renderRotation: (_b = renderPart.rotation) !== null && _b !== void 0 ? _b : 0,
                imageRotation: (_c = image.rotation) !== null && _c !== void 0 ? _c : 0,
                direction,
            });
            const newResource = Object.assign(Object.assign({}, resource), { image: Object.assign(Object.assign({}, image), { cropRect: newCropRect }) });
            return updateResourceInState(state, newResource);
        }
        case ACTIONS.IMAGE_ZOOM_IN:
        case ACTIONS.IMAGE_ZOOM_OUT: {
            const ZOOM_FACTOR = 0.1;
            const MIN_CROP_SIZE = 20;
            const partRef = getPartRefById(action.payload.partRefId, state.template);
            const resource = getResource(partRef, action.payload.resourceId, state);
            if (!resource || resource.type !== "IMAGE")
                return state;
            const renderPart = getFirstRenderPartByResource(resource, state.template);
            if (!renderPart)
                return state;
            const image = resource.image;
            const crop = (_d = image.cropRect) !== null && _d !== void 0 ? _d : {
                x: 0,
                y: 0,
                width: image.width,
                height: image.height,
            };
            const renderRotation = (_e = renderPart.rotation) !== null && _e !== void 0 ? _e : 0;
            const imageRotation = (_f = image.rotation) !== null && _f !== void 0 ? _f : 0;
            const totalRotation = (renderRotation + imageRotation) % 360;
            const isRotated = totalRotation === 90 || totalRotation === 270;
            const imageWidth = isRotated ? image.height : image.width;
            const imageHeight = isRotated ? image.width : image.height;
            const aspectRatio = renderPart.size.width / renderPart.size.height;
            const zoomIn = action.type === ACTIONS.IMAGE_ZOOM_IN;
            const direction = zoomIn ? -1 : 1;
            const centerX = crop.x + crop.width / 2;
            const centerY = crop.y + crop.height / 2;
            let newWidth = crop.width * (1 + direction * ZOOM_FACTOR);
            let newHeight = newWidth / aspectRatio;
            if (newWidth < MIN_CROP_SIZE || newHeight < MIN_CROP_SIZE)
                return state;
            // Prevent zooming out beyond the rotated image bounds
            newWidth = Math.min(newWidth, imageWidth);
            newHeight = Math.min(newHeight, imageHeight);
            let newX = centerX - newWidth / 2;
            let newY = centerY - newHeight / 2;
            newX = Math.max(0, Math.min(newX, imageWidth - newWidth));
            newY = Math.max(0, Math.min(newY, imageHeight - newHeight));
            const newCropRect = {
                x: Math.round(newX),
                y: Math.round(newY),
                width: Math.round(newWidth),
                height: Math.round(newHeight),
            };
            const newResource = Object.assign(Object.assign({}, resource), { image: Object.assign(Object.assign({}, image), { cropRect: newCropRect }) });
            return updateResourceInState(state, newResource);
        }
        case ACTIONS.SET_IMAGE: {
            const resource = action.payload.newResource;
            if (!resource || resource.type !== "IMAGE") {
                return state;
            }
            return updateResourceInState(state, resource);
        }
        case ACTIONS.SET_IMAGE_BACKGROUND: {
            const partRef = getPartRefById(action.payload.partRefId, state.template);
            const resource = getResource(partRef, action.payload.resourceId, state);
            if (!resource || resource.type !== "IMAGE") {
                return state;
            }
            const newResource = Object.assign(Object.assign({}, resource), { image: Object.assign(Object.assign({}, resource.image), { background: action.payload.background }) });
            return updateResourceInState(state, newResource);
        }
        case ACTIONS.SET_IMAGE_EFFECT: {
            partRef = getPartRefById(action.payload.partRefId, state.template);
            resource = getResource(partRef, action.payload.resourceId, state);
            if (!resource || resource.type !== "IMAGE") {
                return state;
            }
            const newResource = Object.assign(Object.assign({}, resource), { image: Object.assign(Object.assign({}, resource.image), { overrideEffect: action.payload.effect }) });
            return updateResourceInState(state, newResource);
        }
        case ACTIONS.PREVIOUS_PARTREF: {
            if (!state.currentPartRefId) {
                return state;
            }
            const partRefIds = getPartRefIds(state.template);
            let currentPartRefIndex = partRefIds.indexOf(state.currentPartRefId);
            if (currentPartRefIndex > 0) {
                currentPartRefIndex--;
            }
            return Object.assign(Object.assign({}, state), { currentPartRefId: partRefIds[currentPartRefIndex], currentResourceId: null });
        }
        case ACTIONS.NEXT_PARTREF: {
            if (!state.currentPartRefId) {
                return state;
            }
            const partRefIds = getPartRefIds(state.template);
            let currentPartRefIndex = partRefIds.indexOf(state.currentPartRefId);
            if (currentPartRefIndex < partRefIds.length - 1) {
                currentPartRefIndex++;
            }
            return Object.assign(Object.assign({}, state), { currentPartRefId: partRefIds[currentPartRefIndex], currentResourceId: null });
        }
        case ACTIONS.SET_PARTREF:
            return Object.assign(Object.assign({}, state), { currentPartRefId: action.partRef.id, currentResourceId: null });
        case ACTIONS.SET_YEAR:
            return Object.assign(Object.assign({}, state), { year: action.year });
    }
    return state;
};
function buildImageObject(baseImage, rotation) {
    return {
        uuid: baseImage.uuid,
        width: baseImage.width,
        height: baseImage.height,
        rotation,
    };
}
function buildNewResourceWithCrop({ resource, image, renderPart, renderPartSize, }) {
    const imageSize = getImageResourceSize(image);
    const pos = getBoxSize(renderPart.image.adaption, imageSize, renderPartSize);
    const cropRect = calculateCropRect(pos, imageSize, renderPartSize);
    return Object.assign(Object.assign({}, resource), { image: Object.assign(Object.assign({}, image), { 
            //loading: true,
            cropRect, overrideEffect: resource.image.overrideEffect || "original", background: resource.image.background }) });
}
function normalizeRenderPartSize(size, rotation, scaleRatio) {
    const scaled = {
        width: size.width * scaleRatio,
        height: size.height * scaleRatio,
    };
    if (rotation === 90 || rotation === 270) {
        return {
            width: scaled.height,
            height: scaled.width,
        };
    }
    return scaled;
}
function rotateImageResource(state, partRefId, resourceId, direction) {
    var _a, _b, _c;
    const partRef = getPartRefById(partRefId, state.template);
    const resource = getResource(partRef, resourceId, state);
    if (!resource || resource.type !== "IMAGE")
        return state;
    const currentRotation = (_a = resource.image.rotation) !== null && _a !== void 0 ? _a : 0;
    const rotationDelta = direction === "left" ? -90 : 90;
    const newRotation = (currentRotation + rotationDelta + 360) % 360;
    const renderPart = getFirstRenderPartByResource(resource, state.template);
    if (!renderPart) {
        return state;
    }
    const renderPartSize = normalizeRenderPartSize(renderPart.size, (_b = renderPart === null || renderPart === void 0 ? void 0 : renderPart.rotation) !== null && _b !== void 0 ? _b : 0, (_c = state.scaleRatio) !== null && _c !== void 0 ? _c : 1);
    const image = buildImageObject(resource.image, newRotation);
    const newResource = buildNewResourceWithCrop({
        resource,
        image,
        renderPart,
        renderPartSize,
    });
    return updateResourceInState(state, newResource);
}
function updateResourceInState(state, updatedResource) {
    return Object.assign(Object.assign({}, state), { resources: Object.assign(Object.assign({}, state.resources), { [updatedResource.fullId]: updatedResource }) });
}
export function createImageDataByImageAndResource(image, resource, template, portraitPictureGravity) {
    var _a, _b, _c, _d;
    const renderPart = getFirstRenderPartByResource(resource, template);
    if (!renderPart || !("image" in renderPart)) {
        return null;
    }
    const rotation = getTemplateImageRotation(template, resource, image);
    const cropRect = getCropRectForImageWithRotation(image.size, renderPart, rotation, portraitPictureGravity);
    const newResource = Object.assign(Object.assign({}, resource), { image: {
            width: image.size.width,
            height: image.size.height,
            uuid: image.uuid,
            cropRect,
            rotation: rotation,
            webshopId: (_a = image.webshopId) !== null && _a !== void 0 ? _a : null,
            overrideEffect: ((_b = resource.image) === null || _b === void 0 ? void 0 : _b.overrideEffect) || "original",
            background: (_c = resource.image) === null || _c === void 0 ? void 0 : _c.background,
            backgroundRotationFollowImage: (_d = resource.image) === null || _d === void 0 ? void 0 : _d.backgroundRotationFollowImage,
            greenscreen: image.greenscreen,
            // FIXME: We should have some kind of image lookup instead
            // of keywords here? It's used when changing all package pictures
            // to check if the picture is allowed different places.
            keywords: image.keywords,
        } });
    return newResource;
}
export function getPartRefAndResource(partRefId, resourceId, templateInstance) {
    if (!resourceId) {
        return null;
    }
    if (!partRefId) {
        return null;
    }
    const partRef = getPartRefById(partRefId, templateInstance.template);
    if (!partRef) {
        return null;
    }
    const resource = getResource(partRef, resourceId, templateInstance);
    if (!resource) {
        return null;
    }
    return {
        partRef: partRef,
        resource: resource,
    };
}
export function getTemplateImageRotation(template, resource, image) {
    const renderPart = getFirstRenderPartByResource(resource, template);
    let imageSize = image.size;
    if (!imageSize) {
        imageSize = {
            width: image.width,
            height: image.height,
        };
    }
    if (!renderPart) {
        return 0;
    }
    let renderPartWidth = renderPart.size.width;
    let renderPartHeight = renderPart.size.height;
    if (renderPart.rotation == 90 || renderPart.rotation == 270) {
        const tmpWidth = renderPartWidth;
        renderPartWidth = renderPartHeight;
        renderPartHeight = tmpWidth;
    }
    const orientation = renderPartWidth > renderPartHeight ? "landscape" : "portrait";
    const imageOrientation = imageSize.width > imageSize.height ? "landscape" : "portrait";
    if (imageOrientation != orientation) {
        return 270;
    }
    return 0;
}
export function getCurrentResource(state) {
    if (!state.currentResourceId || !state.currentPartRefId) {
        return null;
    }
    const partRef = getPartRefById(state.currentPartRefId, state.template);
    const resources = getUserResourcesByPartRef(partRef, state);
    for (const resource of resources) {
        if (resource.fullId == state.currentResourceId) {
            return resource;
        }
    }
    return null;
}
export function getUserResourcesByPartRef(partRef, state) {
    const templateData = getDataByPartRef(partRef, state);
    if (!templateData) {
        return [];
    }
    const cache = {};
    let resource = null;
    const resources = [];
    for (const renderPart of templateData.renderParts) {
        const key = templateData.partRef.id + "#" + renderPart.resourceId;
        if (cache[key]) {
            continue;
        }
        resource = getResource(templateData.partRef, renderPart.resourceId, state);
        // Might be month, monthstring or similar?
        if (!resource) {
            continue;
        }
        if (resource.userSupplied && !resource.disableEdit) {
            resources.push(resource);
        }
        cache[key] = true;
    }
    return resources;
}
function getUserResourcesByPartRefAndType(partRef, state, type) {
    const resources = getUserResourcesByPartRef(partRef, state);
    return resources.filter((resource) => resource.type == type);
}
export function getUserTextResourcesByPartRef(partRef, template) {
    return getUserResourcesByPartRefAndType(partRef, template, "STRING");
}
export function getUserImageResourcesByPartRef(partRef, template) {
    return getUserResourcesByPartRefAndType(partRef, template, "IMAGE");
}
export function getUserImageResources(state) {
    var _a;
    return Object.values((_a = state.resources) !== null && _a !== void 0 ? _a : []).filter((resource) => {
        return (resource.type == "IMAGE" &&
            resource.userSupplied &&
            !resource.disableEdit);
    });
}
export function getImageResourceSize(picture) {
    let size = {
        width: picture.width,
        height: picture.height,
    };
    if (picture.rotation == 90 || picture.rotation == 270) {
        size = { width: size.height, height: size.width };
    }
    return size;
}
export function getFirstRenderPartByResource(resource, template) {
    for (const partRef of template.partRefs) {
        if (partRef.clientIgnore) {
            continue;
        }
        const partType = getPartTypeById(partRef.typeId, template);
        if (!partType) {
            return null;
        }
        for (const renderPart of partType.renderParts) {
            const resourceId = partRef.id + "#" + renderPart.resourceId;
            // Check local and global resources:
            if (resourceId == resource.fullId ||
                renderPart.resourceId == resource.fullId) {
                return renderPart;
            }
        }
    }
}
export function getTemplateTranslation(texts, language) {
    language = language.replace("_", "-");
    const lang2 = language.replace("-", "_");
    if (texts[language]) {
        return removeProducerNote(texts[language]);
    }
    if (texts[lang2]) {
        return removeProducerNote(texts[lang2]);
    }
    if (texts[""]) {
        return removeProducerNote(texts[""]);
    }
    if (texts["DEFAULT"]) {
        return removeProducerNote(texts["DEFAULT"]);
    }
    return "";
}
export function removeProducerNote(str) {
    return str.replace(/\{[^}]*}/g, "");
}
export function getPartRefs(template) {
    return template.partRefs.filter((partRef) => !partRef.clientIgnore);
}
export function getPartRefIds(template) {
    const partRefIds = template.partRefs
        .filter((partRef) => !partRef.clientIgnore)
        .map((partRef) => partRef.id);
    return partRefIds;
}
export function getResource(partRef, resourceId, templateInstance) {
    if (templateInstance === null || templateInstance === void 0 ? void 0 : templateInstance.resources[partRef.id + "#" + resourceId]) {
        return templateInstance.resources[partRef.id + "#" + resourceId];
    }
    // Global resource:
    else if (templateInstance === null || templateInstance === void 0 ? void 0 : templateInstance.resources[resourceId]) {
        return templateInstance.resources[resourceId];
    }
    else {
        return null;
    }
}
export function getPartTypeById(id, template) {
    for (const partType of template.partTypes) {
        if (partType.id === id) {
            return partType;
        }
    }
    return null;
}
export function isTemplateStringResource(resource) {
    const whatIf = resource;
    return (whatIf === null || whatIf === void 0 ? void 0 : whatIf.type) === "STRING";
}
export function isTemplateImageResource(resource) {
    const whatIf = resource;
    return (whatIf === null || whatIf === void 0 ? void 0 : whatIf.type) === "IMAGE";
}
export function resourceIsComplete(resource) {
    var _a;
    if (!resource.userSupplied) {
        return true;
    }
    if (!resource.required) {
        return true;
    }
    if (isTemplateStringResource(resource) && !resource.font.text) {
        console.log("[TEMPLATE] Missing text for resource", resource);
        return false;
    }
    if (isTemplateImageResource(resource) && !((_a = resource.image) === null || _a === void 0 ? void 0 : _a.uuid)) {
        console.log("[TEMPLATE] Missing image for resource", resource);
        return false;
    }
    return true;
}
export function getPartRefIsComplete(partRef, templateInstance) {
    const resources = getUserResourcesByPartRef(partRef, templateInstance);
    for (const resource of resources) {
        if (!resourceIsComplete(resource)) {
            return false;
        }
    }
    return true;
}
export function templateIsComplete(state) {
    for (const resource of Object.values(state.resources)) {
        if (!resourceIsComplete(resource)) {
            return false;
        }
    }
    return true;
}
export function getUserResources(template) {
    const resources = {};
    for (const resource of template.resources) {
        resources[resource.id] = Object.assign({}, resource);
    }
    for (const partRef of template.partRefs.filter((partRef) => !partRef.clientIgnore)) {
        const partType = getPartTypeById(partRef.typeId, template);
        if (!partType) {
            continue;
        }
        for (const originalResource of partType.resources) {
            const resource = Object.assign({}, originalResource);
            resource.fullId = partRef.id + "#" + resource.id;
            resources[resource.fullId] = resource;
        }
    }
    return resources;
}
export function getFontSizeOptions(originalFontSize, currentFontSize) {
    const fontSizes = [];
    for (let j = 10; j > 0; j--) {
        fontSizes.push((1 + -j / 10) * originalFontSize);
    }
    // the "normal" size
    fontSizes.push(originalFontSize);
    for (let j = 1; j <= 10; j++) {
        fontSizes.push((1 + j / 5) * originalFontSize);
    }
    // 2) Find the entry closest to currentFontSize
    let closestKey = null;
    let minFontDiff = Infinity;
    fontSizes.forEach((fontSize, index) => {
        const diff = Math.abs(fontSize - currentFontSize);
        if (diff < minFontDiff) {
            minFontDiff = diff;
            closestKey = index;
        }
    });
    fontSizes[closestKey] = currentFontSize;
    // 4) Build the text labels and return as an array of {value, label}
    return fontSizes.map((fontSize, i) => {
        let label;
        if (i < 10) {
            label = `Smaller -${10 - i}`; // e.g. "Smaller -5"
        }
        else if (i === 10) {
            label = "Normal";
        }
        else {
            label = `Larger +${i - 10}`;
        }
        return {
            value: fontSize,
            label,
        };
    });
}
export function getPartRefById(id, template) {
    const partRef = template.partRefs.find((partRef) => partRef.id == id);
    if (!partRef) {
        throw new Error("Part ref not found");
    }
    return partRef;
}
export function partRefIdIsFirstPartRef(id, template) {
    const partRefIds = getPartRefIds(template);
    return partRefIds.indexOf(id) + 1 == 1;
}
export function partRefIdIsLastPartRef(id, template) {
    const partRefIds = template.partRefs
        .filter((partRef) => !partRef.clientIgnore)
        .map((partRef) => partRef.id);
    return partRefIds.indexOf(id) == partRefIds.length - 1;
}
export function getDataByPartRef(partRef, templateInstance) {
    const partType = getPartTypeById(partRef.typeId, templateInstance.template);
    if (!partType) {
        return null;
    }
    const renderParts = partType.renderParts.map((renderPart) => {
        const resource = getResource(partRef, renderPart.resourceId, templateInstance);
        return renderPart;
    });
    return {
        dimensions: templateInstance.template.dimensions,
        renderParts,
        partRef,
    };
}
const getInitialYear = (template) => {
    if (template.supportedYears && template.supportedYears.length == 1) {
        return template.supportedYears[0];
    }
    const d = new Date();
    let currentYear = d.getFullYear();
    if (d.getMonth() > 5) {
        currentYear++;
    }
    return currentYear;
};
export function createInstance(template, userResources = {}, year, product) {
    return {
        template,
        product: product,
        year: year || getInitialYear(template),
        partOfPackage: false,
        resources: userResources
            ? Object.assign(Object.assign({}, getUserResources(template)), userResources) : getUserResources(template),
        currentPartRefId: getPartRefIds(template)[0],
        currentResourceId: null,
        clientCompatibilityDate: "1900-01-01",
    };
}
const TemplateInstanceProvider = ({ children }) => {
    const [state, dispatch] = useReducer(templateInstanceReducer, {
        template: null,
    });
    return (React.createElement(TemplateInstanceContext.Provider, { value: [state, dispatch] }, children));
};
export default TemplateInstanceProvider;
